import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Save, X, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChangedField {
  field: string;
  label: string;
  oldValue: unknown;
  newValue: unknown;
}

interface PendingChangesCardProps {
  changes: ChangedField[];
  onSave: () => void;
  onDiscard: () => void;
  isSaving: boolean;
  className?: string;
  position?: 'fixed' | 'relative';
  presentation?: 'default' | 'profile';
  /** When true, Save button renders disabled with `saveDisabledReason` as the title/tooltip. */
  saveDisabled?: boolean;
  saveDisabledReason?: string;
  /** Phase 2: render a Cancel button alongside Save/Discard. Only visible if supplied. */
  onCancel?: () => void;
  /** Phase 2: disables the Cancel button while the cancel-group RPC is in flight. */
  isCancelling?: boolean;
  /** Phase 2: renders a small status badge next to the changes-count pill. */
  activeGroupStatus?: string;
  /** Phase 2: when explicitly false, hides the Cancel button even if onCancel supplied. */
  cancelEligible?: boolean;
  /**
   * Domain-specific copy overrides. Defaults preserve the legacy
   * LoanOfficerProfileFormEnhanced strings so existing callers are unaffected.
   * pricing-v2 passes its own pricing-specific labels (QA Follow-up #4).
   */
  title?: string;
  saveLabel?: string;
  discardLabel?: string;
  previousLabel?: string;
  newLabel?: string;
  /**
   * Rendered in place of both the null/undefined "Not set" fallback AND the
   * empty-string "Empty" fallback. Single prop because pricing treats blank
   * and unset identically in the diff display.
   */
  emptyLabel?: string;
}

const STATUS_LABEL: Record<string, { text: string; className: string }> = {
  pending: { text: 'Saving…', className: 'border-blue-400 text-blue-700' },
  pushing: { text: 'Saving…', className: 'border-blue-400 text-blue-700' },
  waiting_unlock: { text: 'Waiting for unlock', className: 'border-amber-400 text-amber-700' },
  waiting_retry: { text: 'Retrying Encompass sync', className: 'border-amber-400 text-amber-700' },
  validated: { text: 'Saved', className: 'border-green-400 text-green-700' },
  cancelled: { text: 'Cancelled', className: 'border-gray-400 text-gray-600' },
  failed: { text: 'Failed', className: 'border-red-400 text-red-700' }
};

export const PendingChangesCard: React.FC<PendingChangesCardProps> = ({
  changes,
  onSave,
  onDiscard,
  isSaving,
  className,
  position = 'fixed',
  presentation = 'default',
  saveDisabled = false,
  saveDisabledReason,
  onCancel,
  isCancelling = false,
  activeGroupStatus,
  cancelEligible,
  title: titleProp,
  saveLabel: saveLabelProp,
  discardLabel = 'Discard',
  previousLabel = 'Previous:',
  newLabel = 'New:',
  emptyLabel
}) => {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const isProfilePresentation = presentation === 'profile';
  const title = titleProp ?? (isProfilePresentation ? 'Pending changes' : 'Unsaved Changes');
  const saveLabel = saveLabelProp ?? (isProfilePresentation ? 'Save all changes' : 'Save All Changes');

  // Phase 2: allow status-only rendering. If there are no pending changes but
  // an active group or cancel control was supplied, keep the card mounted so
  // the user still sees sync status + can cancel pre-push.
  if (changes.length === 0 && !activeGroupStatus && !onCancel) {
    return null;
  }

  const statusBadge = activeGroupStatus ? STATUS_LABEL[activeGroupStatus] : undefined;
  const showCancel = Boolean(onCancel) && cancelEligible !== false;

  const formatValue = (value: unknown): string => {
    // When callers pass `emptyLabel`, use it for both null/undefined AND the
    // explicit-empty-string case. Matches pricing semantics where "blank input"
    // and "not set on loan" render identically.
    if (value === null || value === undefined) {
      return emptyLabel ?? 'Not set';
    }
    if (value === '') {
      return emptyLabel ?? 'Empty';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'None';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };
  
  const getFieldLabel = (field: string): string => {
    const labelMap: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Office Phone',
      mobilePhone: 'Mobile Phone',
      applicationUserId: 'Encompass User ID',
      applicationLink: 'Application Link',
      applicationLinkGeneratedAt: 'Link Generated',
      nmlsNumber: 'NMLS Number',
      title: 'Title',
      bio: 'Bio',
      yearsOfExperience: 'Years of Experience',
      status: 'Employment Status',
      roles: 'User Roles',
      mobileDisplayConsent: 'Display Mobile Phone',
      websiteTemplate: 'Website Template',
      websiteSlug: 'Website URL',
      linkedinUrl: 'LinkedIn',
      facebookUrl: 'Facebook',
      instagramUrl: 'Instagram',
      tiktokUrl: 'TikTok',
      calendarLink: 'Calendar Link'
    };
    return labelMap[field] || field;
  };
  
  return (
    <Card className={cn(
      position === 'fixed' && "bottom-4 right-4 w-96 shadow-xl z-50",
      position === 'relative' && "w-full",
      isProfilePresentation && 'pending-changes-card--profile',
      "border-orange-200 bg-white/95 backdrop-blur",
      className
    )}>
      <CardHeader className={cn('pb-3', isProfilePresentation && 'pending-changes-card__header')}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isProfilePresentation && <AlertTriangle className="h-5 w-5 text-orange-500" />}
            <CardTitle className={isProfilePresentation
              ? 'pending-changes-card__title text-[11px] font-bold uppercase tracking-[0.1em]'
              : 'text-lg'}>
              {title}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {changes.length > 0 && (
              isProfilePresentation ? (
                <span className="pending-changes-card__count font-serif text-[15px]">
                  {changes.length} {changes.length === 1 ? 'change' : 'changes'}
                </span>
              ) : (
                <Badge variant="outline" className="border-orange-400 text-orange-600">
                  {changes.length} {changes.length === 1 ? 'change' : 'changes'}
                </Badge>
              )
            )}
            {statusBadge && (
              <Badge variant="outline" className={statusBadge.className}>
                {statusBadge.text}
              </Badge>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label={isExpanded ? 'Collapse pending changes' : 'Expand pending changes'}
              aria-expanded={isExpanded}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {!isExpanded && (
          <CardDescription className="text-xs mt-1">
            Click to expand and review changes
          </CardDescription>
        )}
      </CardHeader>
      
      {isExpanded && (
        <>
          <CardContent className={cn(
            'max-h-96 overflow-y-auto',
            isProfilePresentation && 'pending-changes-card__content'
          )}>
            {changes.length > 0 ? (
              <div className="space-y-3">
                {changes.map((change, index) => (
                  <div
                    key={`${change.field}-${index}`}
                    className={isProfilePresentation
                      ? 'border-b pb-2 text-xs leading-[1.45] last:border-0 last:pb-0'
                      : 'space-y-1 border-b pb-3 last:border-0'}
                  >
                    <div className="text-sm font-medium text-gray-700">
                      {change.label || getFieldLabel(change.field)}
                    </div>
                    {isProfilePresentation ? (
                      <div className="pending-changes-card__diff break-words">
                        <span className="sr-only">{previousLabel} </span>
                        <span className="line-through">{formatValue(change.oldValue)}</span>
                        <span aria-hidden="true"> → </span>
                        <span className="sr-only">{newLabel} </span>
                        <span className="pending-changes-card__new font-semibold">{formatValue(change.newValue)}</span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="space-y-1">
                          <div className="text-muted-foreground">{previousLabel}</div>
                          <div className="p-2 bg-red-50 rounded text-red-700 break-words">
                            {formatValue(change.oldValue)}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="text-muted-foreground">{newLabel}</div>
                          <div className="p-2 bg-green-50 rounded text-green-700 break-words">
                            {formatValue(change.newValue)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Phase 2: no edits pending, but an active group is still
              // working its way through the state machine. Show the status
              // copy inline so the card reads purposefully instead of empty.
              <div className="py-2 text-sm text-gray-600">
                {statusBadge?.text ?? 'Working…'}
              </div>
            )}
          </CardContent>

          <CardFooter className={cn(
            'gap-2 pt-3',
            isProfilePresentation && 'pending-changes-card__footer'
          )}>
            {changes.length > 0 && (
              <>
                <Button
                  type="button"
                  onClick={onSave}
                  disabled={isSaving || saveDisabled}
                  title={saveDisabled ? saveDisabledReason : undefined}
                  className="flex-1"
                  size="sm"
                >
                  {!isProfilePresentation && <Save className="h-4 w-4 mr-2" />}
                  {saveLabel}
                </Button>
                <Button
                  type="button"
                  onClick={onDiscard}
                  variant="outline"
                  disabled={isSaving}
                  className="flex-1"
                  size="sm"
                >
                  {!isProfilePresentation && <X className="h-4 w-4 mr-2" />}
                  {discardLabel}
                </Button>
              </>
            )}
            {showCancel && (
              <Button
                type="button"
                onClick={onCancel}
                variant="outline"
                disabled={isCancelling}
                className="flex-1"
                size="sm"
              >
                <X className="h-4 w-4 mr-2" />
                {isCancelling ? 'Cancelling…' : 'Cancel Save'}
              </Button>
            )}
          </CardFooter>
        </>
      )}
    </Card>
  );
};
